import {
  createEvoClient,
  getAvailableSurveysForConsumer,
  getConsumerSurveyStatus,
  initializeConsumerSurveyStartedController,
  validateConsumerCanStartSurveyController,
} from "@jti/evo-client";
import { getConfig } from "@jti/fe-common";
import type {
  StartSurveyResponse,
  SurveyApiContract,
  SurveyStatus,
  UserSurvey,
} from "@jti/rest-api-types/types/SurveyApi.types";

import { getUser } from "../../utils";

export class SurveyApi implements SurveyApiContract {
  private readonly client;

  protected constructor(baseUrl: string, tenantToken: string, recaptchaId: string, ciamToken: string) {
    this.client = createEvoClient({
      baseUrl,
      throwOnError: true,
      tenantToken,
      recaptchaId,
      ciamToken,
    });
  }

  static init() {
    const { ciam_token } = getUser();
    const { EVO_DOMAIN, EVO_TENANT, ["X-RECAPTCHA-ID"]: recaptchaId } = getConfig();
    return new SurveyApi(EVO_DOMAIN, EVO_TENANT, recaptchaId, ciam_token);
  }

  async getAvailableSurveys(userUuid: string): Promise<UserSurvey[]> {
    const { data } = await getAvailableSurveysForConsumer({
      client: this.client,
      path: {
        uuid: userUuid,
      },
    });

    return data?.data?.surveys ?? [];
  }

  async getSurveyStatus(userUuid: string, surveyId: string): Promise<SurveyStatus | undefined> {
    const { data } = await getConsumerSurveyStatus({
      client: this.client,
      path: {
        uuid: userUuid,
        survey_id: surveyId,
      },
    });
    return data?.data?.status;
  }

  async startSurvey(userUuid: string, surveyId: string, userInputCode: string): Promise<StartSurveyResponse> {
    const { response } = await initializeConsumerSurveyStartedController({
      client: this.client,
      path: {
        survey_id: surveyId,
        uuid: userUuid,
      },
      body: {
        serial_code: userInputCode,
      },
    });

    return { statusCode: response.status };
  }

  async validateSurveySerialCode(userUuid: string, surveyId: string, userInputCode: string): Promise<void> {
    await validateConsumerCanStartSurveyController({
      client: this.client,
      path: {
        survey_id: surveyId,
        uuid: userUuid,
      },
      body: {
        serial_code: userInputCode,
      },
    });
  }
}
